/**
 * SanityCheckPanel Component
 *
 * Displays proactive model validation warnings with auto-fix capabilities.
 * Uses MCP tools to automatically fix common issues.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import { useAIStore } from '../aiStore';
import { runSanityChecks } from '../sanityChecks';
import { useIDFStore } from '../../stores/idfStore';
import { useMCPTools } from '../../mcp/useMCPTools';
import type { SanityCheckResult } from '../types';

interface SanityCheckPanelProps {
  className?: string;
}

/**
 * Severity icon component
 */
function SeverityIcon({ severity }: { severity: 'info' | 'warning' | 'error' }) {
  const config = {
    info: {
      color: 'text-blue-500 dark:text-blue-400',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    warning: {
      color: 'text-yellow-500 dark:text-yellow-400',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
    },
    error: {
      color: 'text-red-500 dark:text-red-400',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
  }[severity];

  return <span className={config.color}>{config.icon}</span>;
}

/**
 * Individual sanity check result item
 */
function SanityCheckItem({
  result,
  onDismiss,
  onAutoFix,
  isFixing,
}: {
  result: SanityCheckResult;
  onDismiss: () => void;
  onAutoFix: (fixId: string) => void;
  isFixing: boolean;
}) {
  const bgColor = {
    info: 'bg-blue-500/10 dark:bg-blue-900/20 border-blue-400/50 dark:border-blue-800/50 hover:border-blue-500 dark:hover:border-blue-700',
    warning: 'bg-yellow-500/10 dark:bg-yellow-900/20 border-yellow-400/50 dark:border-yellow-800/50 hover:border-yellow-500 dark:hover:border-yellow-700',
    error: 'bg-red-500/10 dark:bg-red-900/20 border-red-400/50 dark:border-red-800/50 hover:border-red-500 dark:hover:border-red-700',
  }[result.severity];

  return (
    <div className={cn('rounded-lg border p-3 transition-colors', bgColor)}>
      <div className="flex items-start gap-3">
        <SeverityIcon severity={result.severity} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h4 className="font-medium text-foreground text-sm">{result.name}</h4>
            <button
              onClick={onDismiss}
              className="text-muted-foreground hover:text-foreground transition-colors p-1"
              title="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{result.message}</p>

          {/* Affected objects */}
          {result.affectedObjects.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {result.affectedObjects.map((obj, i) => (
                <span
                  key={i}
                  className="text-xs px-2 py-0.5 bg-muted rounded text-muted-foreground"
                >
                  {obj}
                </span>
              ))}
            </div>
          )}

          {/* Suggestion */}
          <div className="mt-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Suggestion: </span>
            {result.suggestion}
          </div>

          {/* Auto-fix button */}
          {result.autoFixAvailable && (
            <button
              onClick={() => onAutoFix(result.id)}
              disabled={isFixing}
              className={cn(
                'mt-2 px-3 py-1 text-white text-xs rounded transition-colors flex items-center gap-1.5',
                isFixing
                  ? 'bg-muted text-muted-foreground cursor-wait'
                  : 'bg-green-600 hover:bg-green-700'
              )}
            >
              {isFixing ? (
                <>
                  <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Fixing...
                </>
              ) : (
                <>
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Auto-fix
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Main SanityCheckPanel component
 */
export function SanityCheckPanel({ className }: SanityCheckPanelProps) {
  const sanityCheckResults = useAIStore((state) => state.sanityCheckResults);
  const setSanityCheckResults = useAIStore((state) => state.setSanityCheckResults);
  const setSanityCheckRunning = useAIStore((state) => state.setSanityCheckRunning);
  const isSanityCheckRunning = useAIStore((state) => state.isSanityCheckRunning);
  const dismissSanityCheck = useAIStore((state) => state.dismissSanityCheck);

  const rawContent = useIDFStore((state) => state.rawContent);

  // MCP tools for auto-fix
  const { executeTool, hasModel } = useMCPTools();
  const [fixingId, setFixingId] = useState<string | null>(null);
  const [fixMessage, setFixMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Refs for timeout cleanup
  const recheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messageClearTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (recheckTimeoutRef.current) clearTimeout(recheckTimeoutRef.current);
      if (messageClearTimeoutRef.current) clearTimeout(messageClearTimeoutRef.current);
    };
  }, []);

  // Run sanity checks when content changes
  useEffect(() => {
    if (!rawContent) {
      setSanityCheckResults([]);
      return;
    }

    // Debounce the check
    const timer = setTimeout(() => {
      setSanityCheckRunning(true);
      try {
        const results = runSanityChecks(rawContent);
        setSanityCheckResults(results);
      } finally {
        setSanityCheckRunning(false);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [rawContent, setSanityCheckResults, setSanityCheckRunning]);

  const handleDismiss = useCallback(
    (id: string) => {
      dismissSanityCheck(id);
    },
    [dismissSanityCheck]
  );

  const handleRunChecks = useCallback(() => {
    if (!rawContent) return;

    setSanityCheckRunning(true);
    try {
      const results = runSanityChecks(rawContent);
      setSanityCheckResults(results);
    } finally {
      setSanityCheckRunning(false);
    }
  }, [rawContent, setSanityCheckResults, setSanityCheckRunning]);

  // Handle auto-fix using MCP tools
  const handleAutoFix = useCallback(
    async (fixId: string) => {
      if (!hasModel) {
        setFixMessage({ type: 'error', text: 'No model loaded' });
        return;
      }

      setFixingId(fixId);
      setFixMessage(null);

      try {
        const result = executeTool({
          name: 'apply_auto_fix',
          arguments: { fixId },
        });

        if (result.success) {
          // Remove the fixed issue from the list
          dismissSanityCheck(fixId);
          setFixMessage({
            type: 'success',
            text: `Fixed: ${(result.content as { action?: string })?.action || fixId}`,
          });

          // Re-run sanity checks after fix
          if (recheckTimeoutRef.current) clearTimeout(recheckTimeoutRef.current);
          recheckTimeoutRef.current = setTimeout(() => {
            if (rawContent) {
              const results = runSanityChecks(rawContent);
              setSanityCheckResults(results);
            }
          }, 500);
        } else {
          setFixMessage({
            type: 'error',
            text: result.error || 'Failed to apply fix',
          });
        }
      } catch (error) {
        setFixMessage({
          type: 'error',
          text: error instanceof Error ? error.message : 'Unknown error',
        });
      } finally {
        setFixingId(null);
        // Clear message after 3 seconds
        if (messageClearTimeoutRef.current) clearTimeout(messageClearTimeoutRef.current);
        messageClearTimeoutRef.current = setTimeout(() => setFixMessage(null), 3000);
      }
    },
    [hasModel, executeTool, dismissSanityCheck, rawContent, setSanityCheckResults]
  );

  // Count by severity
  const errorCount = sanityCheckResults.filter((r) => r.severity === 'error').length;
  const warningCount = sanityCheckResults.filter((r) => r.severity === 'warning').length;
  const infoCount = sanityCheckResults.filter((r) => r.severity === 'info').length;

  if (sanityCheckResults.length === 0 && !isSanityCheckRunning) {
    return null;
  }

  return (
    <div className={cn('bg-card rounded-lg overflow-hidden', className)}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-foreground">Model Checks</h3>
          <div className="flex items-center gap-2 text-sm">
            {errorCount > 0 && (
              <span className="text-red-500 dark:text-red-400">{errorCount} errors</span>
            )}
            {warningCount > 0 && (
              <span className="text-yellow-600 dark:text-yellow-400">{warningCount} warnings</span>
            )}
            {infoCount > 0 && (
              <span className="text-blue-500 dark:text-blue-400">{infoCount} info</span>
            )}
          </div>
        </div>
        <button
          onClick={handleRunChecks}
          disabled={isSanityCheckRunning || !rawContent}
          className={cn(
            'px-3 py-1.5 text-sm rounded transition-colors',
            isSanityCheckRunning
              ? 'bg-muted text-muted-foreground cursor-wait'
              : 'bg-secondary hover:bg-secondary/80 text-secondary-foreground'
          )}
        >
          {isSanityCheckRunning ? (
            <span className="flex items-center gap-2">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Checking...
            </span>
          ) : (
            'Re-run Checks'
          )}
        </button>
      </div>

      {/* Fix message */}
      {fixMessage && (
        <div
          className={cn(
            'mx-3 mt-2 px-3 py-2 rounded-lg text-sm',
            fixMessage.type === 'success'
              ? 'bg-green-500/10 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700/50'
              : 'bg-red-500/10 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-700/50'
          )}
        >
          {fixMessage.text}
        </div>
      )}

      {/* Results */}
      <div className="p-3 space-y-2 max-h-96 overflow-y-auto">
        {sanityCheckResults.map((result) => (
          <SanityCheckItem
            key={result.id}
            result={result}
            onDismiss={() => handleDismiss(result.id)}
            onAutoFix={handleAutoFix}
            isFixing={fixingId === result.id}
          />
        ))}
      </div>
    </div>
  );
}

export default SanityCheckPanel;
